import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Step, Question } from '../../types';
import QuestionCard from './QuestionCard';

interface StepColumnProps {
  step: Step;
  questions: Question[];
  stepGradient: string;
  stepTextColor?: string;
  onEditQuestion: (question: Question) => void;
  onDeleteQuestion: (question: Question) => void;
  onAddQuestion: (stepNumber: number) => void;
  onInsertQuestion: (stepNumber: number, insertAtOrder: number) => void;
  deletingQuestionId?: number | null;
  isOperationLoading?: boolean;
  isSingleStepView?: boolean;
  isDragActive?: boolean;
  isReadOnly?: boolean;
  isPresentMode?: boolean;
  // Client-specific props
  isClientView?: boolean;
  userResponses?: Array<{
    id: number;
    user_id: number;
    template_id: number;
    question_id: number;
    response_text: string;
    interval: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    created_at: string;
    updated_at: string;
  }>;
  onAddResponse?: (questionId: number, responseText: string, interval: string) => Promise<void>;
  onEditResponse?: (responseId: number, responseText: string, interval: string) => Promise<void>;
  onDeleteResponse?: (responseId: number) => Promise<void>;
  onQuestionClick?: (questionId: number, questionOrder: number) => void;
}

const StepColumn: React.FC<StepColumnProps> = ({
  step,
  questions,
  stepGradient,
  stepTextColor = '#FFFFFF',
  onEditQuestion,
  onDeleteQuestion,
  onAddQuestion,
  onInsertQuestion,
  deletingQuestionId,
  isOperationLoading = false,
  isSingleStepView = false,
  isDragActive = false,
  isReadOnly = false,
  isPresentMode = false,
  isClientView = false,
  userResponses = [],
  onAddResponse,
  onEditResponse,
  onDeleteResponse,
  onQuestionClick,
}) => {
  const [isColumnHovered, setIsColumnHovered] = React.useState(false);
  
  const { setNodeRef, isOver } = useDroppable({
    id: `step-${step.step_number}`,
    data: {
      type: 'step',
      step,
    },
  });

  return (
    <div 
      className="bg-white border border-gray-200 overflow-hidden flex flex-col h-full min-h-full"
      onMouseEnter={() => setIsColumnHovered(true)}
      onMouseLeave={() => setIsColumnHovered(false)}
    >
      {/* Step Header */}
      <div
        className={`text-left flex-shrink-0 border-b border-gray-200 ${
          isSingleStepView ? 'bg-gray-50 p-6' : 'p-4'
        }`}
        style={!isSingleStepView ? { background: stepGradient } : {}}
      >
        {isSingleStepView ? (
          // Prep Mode: Traditional layout with description
          <>
            <h3 className="text-gray-900 font-bold text-2xl">
              Step {step.step_number}: {step.step_name}
            </h3>
            {step.step_description && (
              <p className="text-gray-600 mt-2 text-sm leading-relaxed">
                {step.step_description}
              </p>
            )}
          </>
        ) : (
          // Present Mode: Gradient header with centered layout, no description
          <div className="text-center" style={{ color: stepTextColor }}>
            <div className="text-sm font-medium opacity-90">
              Step {step.step_number}
            </div>
            <h3 className="text-lg font-bold mt-1">
              {step.step_name}
            </h3>
          </div>
        )}
      </div>

      {/* Questions Area - Flexible content area */}
      <div
        ref={setNodeRef}
        className={`flex-1 flex flex-col transition-colors duration-200 ${
          isSingleStepView ? 'p-6 pt-2' : 'p-4'
        } ${
          isOver ? 'bg-secondary-50 border-2 border-dashed border-secondary-300' : ''
        }`}
      >
        <SortableContext
          items={questions.map(q => `question-${q.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {/* Questions container - grows to fill available space */}
          <div className="flex-1 flex flex-col">
            <div>
              {questions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  stepNumber={step.step_number}
                  onEdit={onEditQuestion}
                  onDelete={onDeleteQuestion}
                  onInsertAt={onInsertQuestion}
                  isDeleting={deletingQuestionId === question.id}
                  showInsertButton={isColumnHovered && !isReadOnly}
                  isSingleStepView={isSingleStepView}
                  isDragActive={isDragActive}
                  isReadOnly={isReadOnly}
                  isPresentMode={isPresentMode}
                  isClientView={isClientView}
                  userResponses={userResponses?.filter(r => r.question_id === question.id) || []}
                  onAddResponse={onAddResponse}
                  onEditResponse={onEditResponse}
                  onDeleteResponse={onDeleteResponse}
                  onQuestionClick={onQuestionClick}
                />
              ))}
              
              {/* Drop Zone Indicator - only when no questions */}
              {isOver && questions.length === 0 && (
                <div className="text-center py-8 text-secondary-600 flex-1 flex flex-col justify-center">
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  <p className="text-sm">Drop question here</p>
                </div>
              )}
            </div>
            
            {/* Add Question Button - Hidden in read-only mode */}
            {!isReadOnly && (
              <button
                  className={`w-full py-3 px-4 border-2 border-dashed rounded-lg text-sm font-medium transition-all duration-200 mt-2 flex-shrink-0 ${
                  isOperationLoading
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-600 hover:border-secondary-400 hover:text-secondary-600 hover:bg-secondary-50'
                  }`}
                  onClick={() => !isOperationLoading && onAddQuestion(step.step_number)}
                  disabled={isOperationLoading}
              >
                  <div className="flex items-center justify-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>{questions.length === 0 ? 'Add Question' : 'Add Question at End'}</span>
                  </div>
              </button>
            )}
          </div>
        </SortableContext>

        {/* Bottom section - always at the bottom */}
        <div className="mt-4 space-y-3 flex-shrink-0">
          {/* Question Count Badge */}
          <div className="text-center">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {questions.length} {questions.length === 1 ? 'question' : 'questions'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepColumn;